import { useEffect, useRef, useState } from 'react';
import type { Broadcast, MetricSample } from '@ivs-test/contracts';
import { create, PlayerEventType, PlayerState, type Player } from 'amazon-ivs-player';
import wasmBinary from 'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm?url';
import wasmWorker from 'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js?url';
import { api } from './api.js';
import { estimateClockOffset, startLatencyDecoder } from './media.js';

interface RemoteQualityStat {
  bytesReceived?: number;
  framesDecoded?: number;
  framesPerSecond?: number;
  jitter?: number;
  packetsLost?: number;
  packetsReceived?: number;
  networkQuality?: number;
}

function networkType(): string | undefined {
  return (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType;
}

export function IvsViewer({ broadcast }: { broadcast: Broadcast }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);
  const [status, setStatus] = useState('停止中');
  const [error, setError] = useState('');
  const [runId, setRunId] = useState('');
  const [latency, setLatency] = useState<number>();
  const [manualLatency, setManualLatency] = useState('');
  const [clientId] = useState(() => `viewer-${crypto.randomUUID()}`);
  const playerRef = useRef<Player | undefined>(undefined);
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const stop = () => {
    cleanupRef.current?.();
    cleanupRef.current = undefined;
    playerRef.current?.delete();
    playerRef.current = undefined;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.removeAttribute('src');
    }
    setStatus('停止中');
  };

  useEffect(() => stop, [broadcast.id]);

  const postSample = (sample: Partial<MetricSample>) => {
    if (!runId) return;
    void api.postTelemetry(runId, [{
      timestamp: new Date().toISOString(),
      clientId,
      role: 'VIEWER',
      userAgent: navigator.userAgent,
      networkType: networkType(),
      ...sample,
    }]);
  };

  const start = async () => {
    stop();
    setError('');
    setStatus('接続中');
    try {
      const credentials = await api.getViewerCredentials(broadcast.id);
      const video = videoRef.current;
      if (!video) throw new Error('Video element is unavailable');
      const clockOffset = await estimateClockOffset();
      const cleanups: Array<() => void> = [];
      if (credentials.kind === 'LOW_LATENCY') {
        if (isIos) {
          video.src = credentials.playbackUrl;
          await video.play();
          setStatus('視聴中（通常遅延）');
          const onWaiting = () => {
            setStatus('バッファ中');
            postSample({ bufferingCount: 1, connectionState: 'BUFFERING' });
          };
          const onPlaying = () => setStatus('視聴中（通常遅延）');
          video.addEventListener('waiting', onWaiting);
          video.addEventListener('playing', onPlaying);
          cleanups.push(() => {
            video.removeEventListener('waiting', onWaiting);
            video.removeEventListener('playing', onPlaying);
          });
        } else {
          const player = create({ wasmWorker, wasmBinary });
          playerRef.current = player;
          player.attachHTMLVideoElement(video);
          player.setAutoplay(true);
          player.setLiveLowLatencyEnabled(true);
          let bufferingStartedAt: number | undefined;
          player.addEventListener(PlayerState.PLAYING, () => {
            setStatus('視聴中');
            if (bufferingStartedAt) {
              postSample({ bufferingDurationMs: Date.now() - bufferingStartedAt, connectionState: 'PLAYING' });
              bufferingStartedAt = undefined;
            }
          });
          player.addEventListener(PlayerState.BUFFERING, () => {
            bufferingStartedAt = Date.now();
            setStatus('バッファ中');
            postSample({ bufferingCount: 1, connectionState: 'BUFFERING' });
          });
          player.addEventListener(PlayerEventType.QUALITY_CHANGED, (quality) => postSample({ quality: quality.name }));
          player.addEventListener(PlayerEventType.ERROR, (playerError) => {
            setError(playerError.message);
            postSample({ errorCode: playerError.code.toString() });
          });
          player.load(credentials.playbackUrl);
          const metricsTimer = window.setInterval(() => {
            const quality = player.getQuality();
            postSample({
              sessionId: player.getSessionId(),
              connectionState: String(player.getState()),
              liveLatencyMs: player.getLiveLatency() * 1000,
              bufferDurationMs: player.getBufferDuration() * 1000,
              bitrateKbps: quality?.bitrate ? quality.bitrate / 1000 : undefined,
              quality: quality?.name,
            });
          }, 10_000);
          cleanups.push(() => window.clearInterval(metricsTimer));
        }
      } else {
        const sdk = await import('amazon-ivs-web-broadcast');
        const strategy = {
          stageStreamsToPublish: () => [],
          shouldPublishParticipant: () => false,
          shouldSubscribeToParticipant: () => sdk.SubscribeType.AUDIO_VIDEO,
        };
        const stage = new sdk.Stage(credentials.participantToken, strategy);
        const remoteStreams: Array<{
          mediaStreamTrack: MediaStreamTrack;
          requestQualityStats(): Promise<RemoteQualityStat[] | undefined>;
        }> = [];
        let connectionState = 'CONNECTING';
        let reconnectCount = 0;
        let previous = { at: Date.now(), bytes: 0, frames: 0 };
        stage.on(sdk.StageEvents.STAGE_CONNECTION_STATE_CHANGED, (nextState) => {
          const next = String(nextState);
          if (connectionState === 'CONNECTED' && next !== 'CONNECTED') reconnectCount += 1;
          connectionState = next;
          postSample({ connectionState: next, reconnectCount: next === 'CONNECTED' ? reconnectCount : 0 });
        });
        stage.on(sdk.StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (_participant, streams) => {
          const qualityStreams = streams as unknown as typeof remoteStreams;
          remoteStreams.splice(0, remoteStreams.length, ...qualityStreams);
          video.srcObject = new MediaStream(streams.map((stream) => stream.mediaStreamTrack));
          void video.play();
          setStatus('視聴中');
        });
        stage.on(sdk.StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (_participant, streams) => {
          const removedTrackIds = new Set(streams.map((stream) => stream.mediaStreamTrack.id));
          for (let index = remoteStreams.length - 1; index >= 0; index -= 1) {
            const stream = remoteStreams[index];
            if (stream && removedTrackIds.has(stream.mediaStreamTrack.id)) remoteStreams.splice(index, 1);
          }
          postSample({ connectionState: 'REMOTE_STREAM_REMOVED', reconnectCount: 1 });
        });
        stage.on(sdk.StageEvents.ERROR, (stageError) => {
          setError(stageError.message);
          postSample({ connectionState, errorCode: String(stageError.code) });
        });
        await stage.join();
        const metricsTimer = window.setInterval(() => {
          void Promise.all(remoteStreams.map((stream) => stream.requestQualityStats())).then((groups) => {
            const stats = groups.flatMap((group) => group ?? []);
            const current = {
              at: Date.now(),
              bytes: stats.reduce((sum, stat) => sum + (stat.bytesReceived ?? 0), 0),
              frames: stats.reduce((sum, stat) => sum + (stat.framesDecoded ?? 0), 0),
            };
            const elapsedSeconds = Math.max(0.001, (current.at - previous.at) / 1000);
            const packetsReceived = stats.reduce((sum, stat) => sum + (stat.packetsReceived ?? 0), 0);
            const packetsLost = stats.reduce((sum, stat) => sum + (stat.packetsLost ?? 0), 0);
            postSample({
              connectionState,
              bitrateKbps: Math.max(0, ((current.bytes - previous.bytes) * 8) / elapsedSeconds / 1000),
              framesPerSecond: stats.find((stat) => stat.framesPerSecond !== undefined)?.framesPerSecond ?? Math.max(0, (current.frames - previous.frames) / elapsedSeconds),
              packetLossPercent: packetsReceived + packetsLost > 0 ? (packetsLost / (packetsReceived + packetsLost)) * 100 : 0,
              jitterMs: Math.max(0, ...stats.map((stat) => (stat.jitter ?? 0) * 1000)),
              quality: stats.map((stat) => String(stat.networkQuality ?? '')).filter(Boolean).join('/'),
            });
            previous = current;
          });
        }, 10_000);
        cleanups.push(() => window.clearInterval(metricsTimer), () => stage.leave());
      }
      cleanups.push(startLatencyDecoder(video, (value) => {
        setLatency(value);
        postSample({ glassToGlassMs: value });
      }, clockOffset));
      cleanupRef.current = () => cleanups.forEach((cleanup) => cleanup());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '視聴の開始に失敗しました');
      setStatus('エラー');
    }
  };

  const recordManualLatency = () => {
    const value = Number(manualLatency);
    if (!Number.isFinite(value) || value < 0) return;
    setLatency(value);
    postSample({ glassToGlassMs: value, quality: 'MANUAL_LATENCY' });
  };

  return (
    <section className="panel">
      <div className="section-heading">
        <div><span className="eyebrow">VIEWER</span><h2>ライブ視聴</h2></div>
        <span className={`status ${status.startsWith('視聴中') ? 'live' : ''}`}>{status}</span>
      </div>
      {isIos && broadcast.kind === 'LOW_LATENCY' && <p className="notice">iOSブラウザではLow-Latency再生が非対応のため、ネイティブHLSによる通常遅延として計測します。</p>}
      <video ref={videoRef} className="video" controls playsInline crossOrigin="use-credentials" />
      <div className="metric-strip"><span>映像QR遅延</span><strong>{latency === undefined ? '—' : `${Math.round(latency)} ms`}</strong></div>
      <div className="form-grid compact">
        <label>計測Run ID（任意）<input value={runId} onChange={(event) => setRunId(event.target.value)} placeholder="UUID" /></label>
        <label>手動遅延 (ms)<input type="number" min={0} value={manualLatency} onChange={(event) => setManualLatency(event.target.value)} placeholder="自動読取できない場合" /></label>
      </div>
      <div className="actions">
        <button className="primary" onClick={() => void start()}>視聴開始</button>
        <button onClick={stop}>停止</button>
        <button onClick={recordManualLatency} disabled={!runId || !manualLatency}>手動値を記録</button>
      </div>
      {error && <p className="error">{error}</p>}
    </section>
  );
}
